import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import WeeklyReportModal, { WeeklyReportsPendingWidget } from "@/pages/crm/WeeklyReportModal";
import { uploadMany, fileUrl } from "@/lib/uploads";
import { pillClass } from "@/lib/factory";
import {
  Check, ChevronRight, Lock, Camera, ShieldCheck, UserPlus, X, DollarSign,
  KeyRound, ClipboardCheck, PaintBucket, Zap, AlertTriangle,
  Building2, Upload, FileText, CalendarClock, BellRing,
  Truck, Loader2, Send, Mic, Square,
} from "lucide-react";
import PendingQueueBanner from "@/components/PendingQueueBanner";
import TeamAssignmentPanel from "@/components/shared/TeamAssignmentPanel";
import TeamSummaryChips, { useProjectTeams } from "@/components/shared/TeamSummaryChips";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

const STAGES = [
  { key: "site_prep", label: "Site Prep & Coverings", num: 1, requires_photo: true },
  { key: "services", label: "False Ceiling / Plumbing / Civil Work", num: 2, requires_pm_approval: true },
  { key: "carcass_delivery", label: "Carcass Delivery", num: 3 },
  { key: "carcass_assembly", label: "Carcass Assembly", num: 4, payment_gate_95: true },
  { key: "component_dispatch", label: "Component Dispatch", num: 5 },
  { key: "onsite_custom", label: "On-Site Customization", num: 6, requires_daily_photo: true },
  { key: "shutters_fitout", label: "Shutters & Hardware", num: 7 },
  { key: "pre_handover_qa", label: "Pre-Handover QA", num: 8, requires_pm_approval: true },
  { key: "handover", label: "Handover / Client Walkthrough", num: 9 },
];

const stagePillTone = (status, isCur) =>
  status === "completed" ? "emerald" : status === "in_progress" || isCur ? "gold" : "gray";

export default function SiteWorkspacePage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const isExecMgr = isAdmin || roleName === "Execution Manager" || roleType === "director";
  const isPm = roleName === "Project Manager";
  const isSup = roleName === "Site Supervisor";
  const isCRMTop = isAdmin || roleType === "director" || roleName === "CRM Manager" || roleName === "CRM Executive";
  const can95 = isAdmin || roleType === "director" || ["Finance Manager", "CRM Manager", "CRM Executive"].includes(roleName);

  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pms, setPms] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [q, setQ] = useState("");

  const filteredProjects = useMemo(
    () => projects.filter((p) => projectMatches(p, q)),
    [projects, q]
  );

  // Feb 24, 2026 · batch team chips for the left sidebar rows.
  const teamsMap = useProjectTeams(filteredProjects.map(p => p.project_id).filter(Boolean));

  const load = useCallback(async () => {
    try {
      const [pr, pmRes, supRes] = await Promise.all([
        api.get("/execution/projects"),
        isExecMgr ? api.get("/users/by-role/Project Manager").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        isExecMgr ? api.get("/users/by-role/Site Supervisor").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setProjects(pr.data);
      setPms(pmRes.data || []);
      setSupervisors(supRes.data || []);
      // First-select initial project via functional setState so `selectedId`
      // doesn't need to be a dep (which would refetch the whole list on every
      // click and clobber stale interval refreshes).
      setSelectedId((prev) => (prev || (pr.data.length > 0 ? pr.data[0].id : null)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [isExecMgr]);
  const loadSelected = useCallback(async (id) => {
    if (!id) return setSelected(null);
    try {
      const { data } = await api.get(`/execution/projects/${id}`);
      setSelected(data);
    } catch { setSelected(null); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);
  useEffect(() => {
    const t = setInterval(() => { load(); if (selectedId) loadSelected(selectedId); }, 25000);
    return () => clearInterval(t);
  }, [load, loadSelected, selectedId]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Phase 7 · Site Execution</p>
        <h1 className="font-display text-3xl text-white mt-1">8-Stage Assembly Engine</h1>
      </div>

      <PendingQueueBanner testid="pending-queue-site"/>

      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 items-start">
        <div className="space-y-2">
          {isCRMTop && (
            <WeeklyReportsPendingWidget onOpen={(pid) => {
              const match = projects.find((p) => p.project_id === pid || p.id === pid);
              if (match) setSelectedId(match.id);
            }}/>
          )}
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={filteredProjects.length}
            totalCount={projects.length}
            testid="site-projects-search"
          />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Sites</p>
          {projects.length === 0 && <div className="iz-card p-6 text-center text-xs text-gray-500">No sites yet.</div>}
          {projects.length > 0 && filteredProjects.length === 0 && q.trim() && (
            <ProjectSearchEmpty query={q} testid="site-projects-search-empty" />
          )}
          {filteredProjects.map(p => (
            <button key={p.id} onClick={() => setSelectedId(p.id)}
                    className={`iz-card p-3 w-full text-left hover:border-[#d4af37]/40 ${selectedId === p.id ? "border-[#d4af37]" : ""}`}
                    data-testid={`exec-row-${p.id}`}>
              <div className="flex items-center justify-between">
                <p className="text-white text-sm truncate">{p.client_name}{p.city ? ` · ${p.city}` : ""}</p>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${pillClass(p.status === "completed" ? "emerald" : p.status === "pending_payment" ? "amber" : "gold")}`}>
                  {p.status?.replace(/_/g, " ")}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 truncate">{p.project_name}</p>
              {p.project_id && (
                <div className="mt-2">
                  <TeamSummaryChips projectId={p.project_id} slots={teamsMap[p.project_id]} />
                </div>
              )}
            </button>
          ))}
        </div>
        <div className="min-w-0">
          {selected ? (
            <Detail sel={selected} pms={pms} supervisors={supervisors}
                    isExecMgr={isExecMgr} isPm={isPm} isSup={isSup} can95={can95}
                    principalId={principal?.id} roleName={roleName}
                    onRefresh={() => { load(); loadSelected(selectedId); }} />
          ) : (
            <div className="iz-card p-12 text-center text-sm text-gray-500">Select a site.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Detail({ sel, pms, supervisors, isExecMgr, isPm, isSup, can95, principalId, roleName, onRefresh, focusStage = null, focusDispatch = null, focusBulk = null, initialTab = null, focusSnag = null }) {
  const [showAssign, setShowAssign] = useState(false);
  // Deep-link · translate the URL `?tab=` into the internal tab key.
  // Accepts: site_initiation → initiation, stages → stages, snags → stages,
  // electrical → electrical, weekly_report(s) → weekly_reports.
  const tabFromDeepLink = (raw) => {
    if (!raw) return null;
    if (raw === "site_initiation") return "initiation";
    if (raw === "weekly_report") return "weekly_reports";
    if (raw === "snags") return "stages";           // snags surface inside the stages tab
    if (["initiation", "electrical", "stages", "weekly_reports"].includes(raw)) return raw;
    return null;
  };
  const [tab, setTab] = useState(tabFromDeepLink(initialTab) || "initiation");
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);

  // Deep-link support — jump straight to the correct tab when arriving via a
  // Pending-Action / notification click. focusStage still forces the Stages tab.
  useEffect(() => {
    const mapped = tabFromDeepLink(initialTab);
    if (mapped) setTab(mapped);
    if (focusStage) setTab("stages");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusStage, initialTab]);

  // Auto-scroll + flash the deep-linked snag row once the stages tab has mounted.
  useEffect(() => {
    if (!focusSnag) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="snag-row-${focusSnag}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("iz-flash-highlight");
        setTimeout(() => el.classList.remove("iz-flash-highlight"), 2600);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [focusSnag, tab]);

  const flip95 = async () => {
    try {
      await api.post(`/execution/projects/${sel.id}/accounts-95`);
      toast.success("95% payment confirmed — downstream unlocked");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Feb 24, 2026 — write access for the initiation checklist + electrical
  // meeting plan now includes CRM Executive (not just Site Supervisor / PM /
  // Execution Manager). Navigation between tabs is never blocked.
  const isCRM = roleName === "CRM Manager" || roleName === "CRM Executive" || isExecMgr;
  const canEditInit =
    isExecMgr ||
    (isSup && sel.site_supervisor_id === principalId) ||
    (isPm && sel.project_manager_id === principalId) ||
    isCRM;
  const canPlanMeeting =
    isExecMgr || isPm || isSup || isCRM ||
    ["Interior Designer", "Design Manager", "Design Head"].includes(roleName);

  const TABS = [
    { key: "initiation",  label: "Site Initiation",  icon: ClipboardCheck },
    { key: "electrical",  label: "Electrical Meeting Plan", icon: Zap },
    { key: "stages",      label: "Execution Stages", icon: Building2 },
    ...(isCRM ? [{ key: "weekly_reports", label: "Weekly Reports", icon: FileText }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Execution Project</p>
            <h2 className="font-display text-2xl text-white mt-1">{sel.client_name}{sel.city ? ` · ${sel.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{sel.project_name}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {sel.accounts_receipt_95_percent && <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-1 rounded flex items-center gap-1"><DollarSign className="w-3 h-3"/>95% Paid</span>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-4 border-t border-[#1a1a1a]">
          <Meta label="Project Manager" value={pms.find(u => u.id === sel.project_manager_id)?.name || "—"}/>
          <Meta label="Site Supervisor" value={supervisors.find(u => u.id === sel.site_supervisor_id)?.name || "—"}/>
          <Meta label="Status" value={sel.status?.replace(/_/g, " ")}/>
        </div>

        <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex gap-2 flex-wrap">
          {isExecMgr && (
            <button onClick={() => setShowAssign(true)} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="assign-btn">
              <UserPlus className="w-3.5 h-3.5"/>{sel.project_manager_id ? "Reassign" : "Assign PM & Supervisor"}
            </button>
          )}
          {isCRM && (
            <button onClick={() => setShowWeeklyReport(true)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="generate-weekly-report-btn">
              <FileText className="w-3.5 h-3.5"/> Generate Weekly Report
            </button>
          )}
          {can95 && sel.status === "pending_payment" && !sel.accounts_receipt_95_percent && (
            <button onClick={flip95} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="flip-95">
              <DollarSign className="w-3.5 h-3.5"/>Confirm 95% Payment
            </button>
          )}
        </div>
      </div>

      {showWeeklyReport && (
        <WeeklyReportModal projectId={sel.project_id}
                           onClose={() => setShowWeeklyReport(false)}
                           onSent={() => { setShowWeeklyReport(false); onRefresh(); }}/>
      )}

      {/* Tab bar — every tab is always clickable. Never gate navigation. */}
      <div className="iz-card p-2 flex flex-wrap gap-1" data-testid="site-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-2 rounded-md text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                    tab === t.key
                      ? "bg-[#d4af37] text-black"
                      : "text-gray-400 hover:text-white hover:bg-[#111]"
                  }`}
                  data-testid={`tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </button>
        ))}
      </div>

      {tab === "initiation" && (
        <InitiationChecklist sel={sel} canEdit={canEditInit} onRefresh={onRefresh}/>
      )}
      {tab === "electrical" && (
        <ElectricalMeetingPanel sel={sel} canEdit={canPlanMeeting}
                                 roleName={roleName} principalId={principalId}
                                 onRefresh={onRefresh}/>
      )}
      {tab === "stages" && (
        <>
          {sel.project_id && <TeamAssignmentPanel projectId={sel.project_id} onChanged={onRefresh} />}
          <ResourcesPanel sel={sel} canEdit={isExecMgr || isPm || (isSup && sel.site_supervisor_id === principalId)} onRefresh={onRefresh} />
          <StagesPipeline sel={sel} isExecMgr={isExecMgr} isPm={isPm} isSup={isSup} principalId={principalId} onRefresh={onRefresh}
                          focusStage={focusStage} focusDispatch={focusDispatch} focusBulk={focusBulk}/>
          {isExecMgr && sel.status === "handover" && (
            <QASignoffPanel sel={sel} onRefresh={onRefresh}/>
          )}
        </>
      )}
      {tab === "weekly_reports" && (
        <WeeklyReportsHistoryPanel projectId={sel.project_id} onOpen={() => setShowWeeklyReport(true)}/>
      )}

      {showAssign && (
        <AssignModal sel={sel} pms={pms} supervisors={supervisors}
                     onClose={() => setShowAssign(false)}
                     onDone={() => { setShowAssign(false); onRefresh(); }}/>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">{label}</p>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}

// Feb 24, 2026 · unified checklist. Every item saves independently via
// `PATCH /execution/projects/{id}/initiation`. No "Submit" button — the UI
// never blocks the user from navigating to the next tab.
function InitiationChecklist({ sel, canEdit, onRefresh }) {
  const init = sel.site_initiation || {};
  // Normalize legacy stored values (confirmed/approved/deployed → yes,
  // pending/not_deployed → no) so old records still render cleanly.
  const yn = (v, positive) => {
    if (v == null) return null;
    if (["yes", ...positive].includes(v)) return "yes";
    if (["no", "pending", "not_deployed"].includes(v)) return "no";
    return null;
  };
  const [busyKey, setBusyKey] = useState("");
  const [officeDraft, setOfficeDraft] = useState(init.key_collection_office || "");
  useEffect(() => { setOfficeDraft(init.key_collection_office || ""); }, [init.key_collection_office]);

  const patch = async (updates, keyForBusy) => {
    setBusyKey(keyForBusy || Object.keys(updates)[0]);
    try {
      await api.patch(`/execution/projects/${sel.id}/initiation`, updates);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyKey("");
    }
  };

  const uploadFile = async (accept, saveKey, fileType) => {
    return new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = accept;
      input.onchange = async () => {
        const f = input.files?.[0];
        if (!f) return resolve(null);
        setBusyKey(saveKey);
        try {
          const u = await uploadMany([f]);
          const fid = u[0]?.id;
          if (fid) {
            await patch({ [saveKey]: fid }, saveKey);
            toast.success(`${fileType} uploaded`);
          }
          resolve(fid);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
          resolve(null);
        } finally { setBusyKey(""); }
      };
      input.click();
    });
  };

  const keyStatus = yn(init.key_collection_status, ["confirmed"]);
  const permitStatus = yn(init.work_permit_status, ["approved"]);
  const coveringDone = init.floor_covering_status === "completed" || init.floor_covering_status === "deployed";
  const accessVerified = yn(init.site_access_verified, []);
  const powerWater = yn(init.power_water_available, []);

  return (
    <div className="iz-card p-6 space-y-5" data-testid="initiation-checklist">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-[#d4af37]"/>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Site Initiation Checklist · saves per item · never blocks navigation
        </p>
      </div>
      {!canEdit && (
        <p className="text-[11px] font-mono text-gray-500">
          Read-only for your role · CRM Executive, Site Supervisor, PM, and Execution Manager can edit.
        </p>
      )}

      {/* 1. Key Collection */}
      <ChecklistRow icon={KeyRound} label="Key Collection Status"
                     value={keyStatus} kind="yn" disabled={!canEdit || busyKey === "key_collection_status"}
                     onChange={(v) => patch({ key_collection_status: v })}
                     testid="key-collection">
        {keyStatus === "yes" && (
          <div className="mt-2 flex gap-2 items-center" data-testid="key-collection-office-block">
            <input value={officeDraft} onChange={(e) => setOfficeDraft(e.target.value)}
                   disabled={!canEdit}
                   placeholder="Office / location where the key is available"
                   className="iz-input text-xs !py-1 flex-1"
                   data-testid="key-collection-office"/>
            {canEdit && (
              <button
                disabled={busyKey === "key_collection_office" || officeDraft === (init.key_collection_office || "")}
                onClick={() => patch({ key_collection_office: officeDraft.trim() }, "key_collection_office")}
                className="iz-btn-secondary text-xs" data-testid="key-collection-office-save">
                Save
              </button>
            )}
          </div>
        )}
      </ChecklistRow>

      {/* 2. Work Permit */}
      <ChecklistRow icon={ClipboardCheck} label="Work Permit Status"
                     value={permitStatus} kind="yn" disabled={!canEdit || busyKey === "work_permit_status"}
                     onChange={(v) => patch({ work_permit_status: v })}
                     testid="work-permit">
        {permitStatus === "yes" && (
          <div className="mt-2 flex gap-2 items-center flex-wrap" data-testid="work-permit-file-block">
            {init.work_permit_file_id ? (
              <a href={fileUrl(init.work_permit_file_id)} target="_blank" rel="noreferrer"
                 className="text-xs text-[#d4af37] flex items-center gap-1.5 hover:underline"
                 data-testid="work-permit-file-link">
                <FileText className="w-3.5 h-3.5"/>Uploaded permit
              </a>
            ) : (
              <span className="text-[11px] font-mono text-gray-500">No file uploaded yet (optional)</span>
            )}
            {canEdit && (
              <button disabled={busyKey === "work_permit_file_id"}
                onClick={() => uploadFile("application/pdf,image/*", "work_permit_file_id", "Permit")}
                className="iz-btn-secondary text-xs flex items-center gap-1.5"
                data-testid="work-permit-upload">
                <Upload className="w-3.5 h-3.5"/>{init.work_permit_file_id ? "Replace" : "Upload permit"}
              </button>
            )}
          </div>
        )}
      </ChecklistRow>

      {/* 3. Floor Covering */}
      <ChecklistRow icon={PaintBucket} label="Floor Covering Deployment"
                     value={coveringDone ? "completed" : (init.floor_covering_status ? "pending" : null)}
                     kind="cp"
                     disabled={!canEdit || busyKey === "floor_covering_status"}
                     onChange={(v) => patch({ floor_covering_status: v })}
                     testid="floor-covering">
        {coveringDone && (
          <div className="mt-2 flex gap-2 items-center flex-wrap" data-testid="floor-covering-photo-block">
            {init.floor_covering_photo_id ? (
              <a href={fileUrl(init.floor_covering_photo_id)} target="_blank" rel="noreferrer"
                 className="block w-14 h-14 bg-[#0a0a0a] border border-[#1a1a1a] rounded overflow-hidden">
                <img src={fileUrl(init.floor_covering_photo_id)} alt="" className="w-full h-full object-cover"/>
              </a>
            ) : (
              <span className="text-[11px] font-mono text-gray-500">No photo uploaded yet (optional)</span>
            )}
            {canEdit && (
              <button disabled={busyKey === "floor_covering_photo_id"}
                onClick={() => uploadFile("image/*", "floor_covering_photo_id", "Photo")}
                className="iz-btn-secondary text-xs flex items-center gap-1.5"
                data-testid="floor-covering-upload">
                <Camera className="w-3.5 h-3.5"/>{init.floor_covering_photo_id ? "Replace photo" : "Upload photo"}
              </button>
            )}
          </div>
        )}
      </ChecklistRow>

      {/* 4. Site Access */}
      <ChecklistRow icon={ShieldCheck} label="Site Access & Clearances Verified"
                     value={accessVerified} kind="yn" disabled={!canEdit || busyKey === "site_access_verified"}
                     onChange={(v) => patch({ site_access_verified: v })}
                     testid="site-access"/>

      {/* 5. Power & Water */}
      <ChecklistRow icon={Zap} label="Power & Water Supply Available"
                     value={powerWater} kind="yn" disabled={!canEdit || busyKey === "power_water_available"}
                     onChange={(v) => patch({ power_water_available: v })}
                     testid="power-water"/>

      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-600 pt-3 border-t border-[#1a1a1a]">
        Checklist state reflects live in the Supervisor tab · no submission required
      </p>
    </div>
  );
}


function ChecklistRow({ icon: Icon, label, value, kind, disabled, onChange, children, testid }) {
  // kind: "yn" → Yes/No · "cp" → Completed/Pending
  const opts = kind === "yn"
    ? [{ v: "yes", label: "Yes" }, { v: "no", label: "No" }]
    : [{ v: "completed", label: "Completed" }, { v: "pending", label: "Pending" }];
  return (
    <div className="pb-4 border-b border-[#1a1a1a] last:border-b-0 last:pb-0" data-testid={`row-${testid}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-white text-sm">
          <Icon className="w-4 h-4 text-[#d4af37]"/> {label}
        </div>
        <div className="flex gap-1" role="tablist">
          {opts.map((o) => {
            const active = value === o.v;
            return (
              <button key={o.v} disabled={disabled} onClick={() => onChange(o.v)}
                      className={`px-3 py-1 rounded-md text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                        active
                          ? "border-[#d4af37] bg-[#d4af37]/10 text-[#d4af37]"
                          : "border-[#262626] text-gray-500 hover:text-white"
                      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`${testid}-${o.v}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
      {children}
    </div>
  );
}


// Feb 24, 2026 · Electrical Meeting Plan tab.
function ElectricalMeetingPanel({ sel, canEdit, roleName, principalId, onRefresh }) {
  const plan = sel.electrical_meeting || null;
  const [meetingDate, setMeetingDate] = useState(plan?.meeting_date || "");
  const [meetingTime, setMeetingTime] = useState(plan?.meeting_time || "10:00");
  const [notes, setNotes] = useState(plan?.notes || "");
  const [walkNotes, setWalkNotes] = useState(plan?.walkthrough_notes || "");
  const [saving, setSaving] = useState(false);
  const [walkSaving, setWalkSaving] = useState(false);
  const [acking, setAcking] = useState(false);

  useEffect(() => {
    setMeetingDate(plan?.meeting_date || "");
    setMeetingTime(plan?.meeting_time || "10:00");
    setNotes(plan?.notes || "");
    setWalkNotes(plan?.walkthrough_notes || "");
  }, [plan?.meeting_date, plan?.meeting_time, plan?.notes, plan?.walkthrough_notes]);

  const savePlan = async () => {
    if (!meetingDate || !meetingTime) {
      toast.error("Please pick a date and time");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/execution/projects/${sel.id}/electrical-meeting`, {
        meeting_date: meetingDate,
        meeting_time: meetingTime,
        notes,
      });
      toast.success(plan ? "Electrical meeting updated" : "Electrical meeting planned — Client & Designer notified");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const saveWalkNotes = async () => {
    setWalkSaving(true);
    try {
      await api.patch(`/execution/projects/${sel.id}/electrical-meeting/walkthrough-notes`,
                       { walkthrough_notes: walkNotes });
      toast.success("Walkthrough notes saved");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWalkSaving(false); }
  };

  const acknowledge = async () => {
    setAcking(true);
    try {
      await api.post(`/execution/projects/${sel.id}/electrical-meeting/acknowledge`);
      toast.success("Acknowledged");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setAcking(false); }
  };

  const isDesigner = ["Interior Designer", "Design Manager", "Design Head"].includes(roleName);
  const alreadyAcked = plan && (isDesigner ? plan.designer_ack : plan.client_ack);

  return (
    <div className="iz-card p-6 space-y-5" data-testid="electrical-meeting-panel">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-[#d4af37]"/>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Electrical Meeting Plan · CRM + Supervisor
        </p>
      </div>

      {!canEdit && (
        <p className="text-[11px] font-mono text-gray-500">
          Read-only for your role.{isDesigner ? " You can still acknowledge below." : ""}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
            Meeting Date
          </label>
          <input type="date" value={meetingDate} disabled={!canEdit}
                 onChange={(e) => setMeetingDate(e.target.value)}
                 className="iz-input text-sm" data-testid="elec-meeting-date"/>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
            Meeting Time
          </label>
          <input type="time" value={meetingTime} disabled={!canEdit}
                 onChange={(e) => setMeetingTime(e.target.value)}
                 className="iz-input text-sm" data-testid="elec-meeting-time"/>
        </div>
        <div className="flex items-end">
          {canEdit && (
            <button onClick={savePlan} disabled={saving}
                    className="iz-btn-primary text-xs flex items-center gap-1.5 w-full justify-center"
                    data-testid="elec-meeting-save">
              <CalendarClock className="w-3.5 h-3.5"/>
              {saving ? "Saving…" : plan ? "Update plan" : "Plan meeting"}
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
          Notes for the meeting
        </label>
        <textarea rows={3} value={notes} disabled={!canEdit}
                  onChange={(e) => setNotes(e.target.value)}
                  className="iz-input text-sm"
                  placeholder="Agenda, points to raise with the electrician, escalations…"
                  data-testid="elec-meeting-notes"/>
      </div>

      {plan && (
        <div className="pt-4 border-t border-[#1a1a1a] space-y-3" data-testid="elec-meeting-summary">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Planned:</span>
            <span className="text-sm text-white">
              {plan.meeting_date} · {plan.meeting_time}
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              by {plan.planned_by_role || "—"}
            </span>
            <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${plan.designer_ack ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/5" : "border-[#262626] text-gray-500"}`}
                  data-testid="designer-ack-pill">
              Designer {plan.designer_ack ? "✓ ack" : "pending ack"}
            </span>
            <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${plan.client_ack ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/5" : "border-[#262626] text-gray-500"}`}
                  data-testid="client-ack-pill">
              Client {plan.client_ack ? "✓ ack" : "pending ack"}
            </span>
          </div>

          {isDesigner && !alreadyAcked && (
            <button onClick={acknowledge} disabled={acking}
                    className="iz-btn-secondary text-xs flex items-center gap-1.5"
                    data-testid="elec-ack-designer">
              <BellRing className="w-3.5 h-3.5"/>
              {acking ? "Acknowledging…" : "Acknowledge (optional — you can dismiss instead)"}
            </button>
          )}

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">
              Walkthrough observations (edit anytime)
            </label>
            <textarea rows={4} value={walkNotes} disabled={!canEdit}
                      onChange={(e) => setWalkNotes(e.target.value)}
                      className="iz-input text-sm"
                      placeholder="Observations captured during/after the electrical walkthrough…"
                      data-testid="elec-walk-notes"/>
            {canEdit && (
              <div className="mt-2 flex justify-end">
                <button onClick={saveWalkNotes} disabled={walkSaving}
                        className="iz-btn-secondary text-xs"
                        data-testid="elec-walk-notes-save">
                  {walkSaving ? "Saving…" : "Save observations"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResourcesPanel({ sel, canEdit, onRefresh }) {
  const [state, setState] = useState({ false_ceiling: "", electrician: "", carpenter: "" });
  const [history, setHistory] = useState({ false_ceiling: [], electrician: [], carpenter: [] });

  // Load per-project history — never global, never auto-filled.
  useEffect(() => {
    if (!sel?.id) return;
    api.get(`/execution/projects/${sel.id}/resource-history`)
      .then(({ data }) => setHistory(data || { false_ceiling: [], electrician: [], carpenter: [] }))
      .catch(() => setHistory({ false_ceiling: [], electrician: [], carpenter: [] }));
  }, [sel?.id, sel?.resources]);

  const save = async (resource_type) => {
    const workers = state[resource_type].split(",").map(s => s.trim()).filter(Boolean);
    try {
      await api.patch(`/execution/projects/${sel.id}/resources`, { resource_type, workers });
      toast.success("Resources saved");
      onRefresh();
      setState({ ...state, [resource_type]: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="iz-card p-5" data-testid="resources-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-1">Resource Teams</p>
      <p className="text-[10px] text-gray-500 italic mb-3">Names you've assigned before on this project are remembered for one-tap reuse.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {["false_ceiling", "electrician", "carpenter"].map(k => (
          <div key={k}>
            <p className="text-[10px] font-mono uppercase text-gray-400 mb-1">{k.replace("_", " ")}</p>
            <div className="flex flex-wrap gap-1 mb-2">
              {(sel.resources?.[k] || []).length === 0 && <span className="text-[10px] font-mono text-gray-600 italic">no team</span>}
              {(sel.resources?.[k] || []).map((w, i) => (
                <span key={`${w}-${i}`} className="text-[10px] font-mono text-[#d4af37] border border-[#d4af37]/30 px-1.5 py-0.5 rounded">{w}</span>
              ))}
            </div>
            {canEdit && (
              <>
                {(history[k] || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {history[k].map((name) => {
                      const already = (state[k].split(",").map(s => s.trim())).includes(name);
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => {
                            const parts = state[k].split(",").map(s => s.trim()).filter(Boolean);
                            if (parts.includes(name)) {
                              setState({ ...state, [k]: parts.filter(x => x !== name).join(", ") });
                            } else {
                              setState({ ...state, [k]: [...parts, name].join(", ") });
                            }
                          }}
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                            already
                              ? "bg-[#d4af37]/20 border-[#d4af37] text-[#d4af37]"
                              : "border-[#262626] text-gray-400 hover:border-[#d4af37]/40 hover:text-white"
                          }`}
                          data-testid={`res-history-${k}-${name}`}
                        >
                          {already ? "✓ " : "+ "}{name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-1">
                  <input value={state[k]} onChange={e => setState({ ...state, [k]: e.target.value })}
                         list={`res-history-list-${sel.id}-${k}`}
                         placeholder="Type new name or pick above"
                         className="iz-input text-xs !py-1" data-testid={`res-input-${k}`}/>
                  <button onClick={() => save(k)} className="iz-btn-secondary text-xs" data-testid={`res-save-${k}`}>Set</button>
                </div>
                <datalist id={`res-history-list-${sel.id}-${k}`}>
                  {(history[k] || []).map((name) => <option key={name} value={name} />)}
                </datalist>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StagesPipeline({ sel, isExecMgr, isPm, isSup, principalId, onRefresh, focusStage = null, focusDispatch = null, focusBulk = null }) {
  // Feb 2026 · Default to (1) the deep-link `focusStage` if valid, else (2)
  // the first in-progress stage, else (3) Stage 1.
  const defaultIdx = useMemo(() => {
    const stages = sel.execution_stages || [];
    if (focusStage) {
      const focusIdx = STAGES.findIndex((s) => s.key === focusStage);
      if (focusIdx >= 0) return focusIdx;
    }
    const active = stages.findIndex((st) => st?.status === "in_progress");
    return active >= 0 ? active : 0;
  }, [sel.execution_stages, focusStage]);
  const [expandedIdx, setExpandedIdx] = useState(defaultIdx);

  // Re-sync when the underlying project changes (initial land or refresh)
  useEffect(() => { setExpandedIdx(defaultIdx); }, [sel.id, defaultIdx]);

  return (
    <div className="space-y-3" data-testid="stages-pipeline">
      <div className="iz-card p-4 flex items-center gap-1 overflow-x-auto">
        {STAGES.map((s, i) => {
          const stage = sel.execution_stages[i];
          const tone = stagePillTone(stage?.status, expandedIdx === i);
          return (
            <React.Fragment key={s.key}>
              <button onClick={() => setExpandedIdx(i)}
                      className={`px-2.5 py-1 rounded border text-[9px] font-mono uppercase tracking-widest whitespace-nowrap ${pillClass(tone)}`}
                      data-testid={`stage-step-${s.key}`}>
                {s.num}. {s.label}
              </button>
              {i < STAGES.length - 1 && <ChevronRight className="w-3 h-3 text-gray-700 shrink-0"/>}
            </React.Fragment>
          );
        })}
      </div>
      <StageDetail sel={sel} stageIdx={expandedIdx} isExecMgr={isExecMgr} isPm={isPm} isSup={isSup}
                   principalId={principalId} onRefresh={onRefresh}
                   focusDispatch={focusDispatch} focusBulk={focusBulk}/>
    </div>
  );
}

function StageDetail({ sel, stageIdx, isExecMgr, isPm, isSup, principalId, onRefresh, focusDispatch = null, focusBulk = null }) {
  const s = STAGES[stageIdx];
  const stage = sel.execution_stages[stageIdx] || { status: "not_started", files: [], daily_photos: [], notes: "" };
  const inputRef = useRef(null);
  const dailyRef = useRef(null);
  const captureRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState(stage.notes || "");
  const [notesDirty, setNotesDirty] = useState(false);

  // Feb 2026 — Voice note dictation via MediaRecorder + OpenAI Whisper.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRec = useRef(null);
  const audioChunks = useRef([]);

  // Keep local notes in sync when the user switches between stage tabs.
  useEffect(() => {
    setNotes(stage.notes || "");
    setNotesDirty(false);
  }, [stageIdx, stage.notes]);

  // Feb 2026 — Stage 5 "Component Dispatch" is driven by
  // Production/Procurement/Purchase actions, not by supervisor input. The
  // supervisor/PM sees the dispatch log from the factory + a Material Request
  // form (photo + note) that pings the Purchase team.
  //
  // NOTE: this early return sits AFTER all hook calls above so React's
  // rules-of-hooks stay satisfied on every render path.
  if (s.key === "component_dispatch") {
    return <ComponentDispatchPanel sel={sel} stageKey={s.key} stageNum={s.num} stageLabel={s.label}
                                   isSup={isSup} isPm={isPm} isExecMgr={isExecMgr}
                                   principalId={principalId} onRefresh={onRefresh}
                                   focusDispatch={focusDispatch} focusBulk={focusBulk}/>;
  }

  // Feb 2026 · every assigned role (PM, Supervisor, Execution Manager) can
  // update any stage independently — no Site Initiation prerequisite, no
  // linear "prior stage first" gate.
  const canWrite =
    isExecMgr ||
    (isSup && sel.site_supervisor_id === principalId) ||
    (isPm && sel.project_manager_id === principalId);

  const setStatus = async (status) => {
    let noteToSave = notesDirty ? notes : undefined;
    // Feb 2026 · when a supervisor flips a stage to Blocked, we ALWAYS prompt
    // for a one-line reason so PMs see the blocker on the project card
    // without opening the project detail.
    if (status === "blocked") {
      const existing = (stage.notes || "").trim();
      const promptDefault = existing || "";
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        "Why is this stage blocked?\n(One line — Project Managers will see this on the card.)",
        promptDefault
      );
      if (reason === null) return; // user cancelled — do NOT change status
      const trimmed = reason.trim();
      if (!trimmed) {
        toast.error("A short reason is required when marking a stage as Blocked.");
        return;
      }
      noteToSave = trimmed;
      setNotes(trimmed);
      setNotesDirty(false);
    }
    setBusy(true);
    try {
      const body = { status };
      if (noteToSave !== undefined) body.note = noteToSave;
      await api.patch(`/execution/projects/${sel.id}/stages/${s.key}`, body);
      toast.success(`Stage → ${status.replace(/_/g, " ")}`);
      setNotesDirty(false);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    if (!notesDirty) return;
    setBusy(true);
    try {
      await api.patch(`/execution/projects/${sel.id}/stages/${s.key}`,
                      { status: stage.status || "not_started", note: notes });
      toast.success("Notes saved");
      setNotesDirty(false);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (files, daily = false) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const u = await uploadMany(Array.from(files));
      await api.post(`/execution/projects/${sel.id}/stages/${s.key}/attachments`,
                     { file_ids: u.map(x => x.id), daily });
      toast.success("Photo uploaded");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Feb 2026 · voice dictation — record with MediaRecorder, transcribe via
  // /api/voice/transcribe (OpenAI Whisper), append the English transcript to
  // the Stage Notes textarea and persist through the existing stage patch.
  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks.current = [];
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      rec.ondataavailable = (e) => { if (e.data.size) audioChunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        await handleRecordedBlob(new Blob(audioChunks.current, { type: "audio/webm" }));
      };
      rec.start();
      mediaRec.current = rec;
      setRecording(true);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };
  const stopRecord = () => {
    if (mediaRec.current && recording) { mediaRec.current.stop(); setRecording(false); }
  };
  const handleRecordedBlob = async (blob) => {
    setTranscribing(true);
    try {
      const audioFile = new File([blob], `stage-voice-${Date.now()}.webm`, { type: "audio/webm" });
      const fd = new FormData();
      fd.append("file", audioFile);
      const { data } = await api.post("/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const english = (data.text || "").trim();
      if (english) {
        const next = (notes ? notes + "\n" : "") + english;
        setNotes(next);
        setNotesDirty(true);
        toast.success(`Voice note transcribed (${data.detected_language || "auto"})`);
      } else {
        toast.info("No speech detected");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transcription failed");
    } finally { setTranscribing(false); }
  };
  const pmApprove = async () => {
    try {
      await api.post(`/execution/projects/${sel.id}/stages/${s.key}/pm-approve`);
      toast.success("PM approved");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const STATUS_OPTIONS = [
    { value: "not_started", label: "Not started" },
    { value: "in_progress", label: "In progress" },
    { value: "completed",   label: "Completed" },
    { value: "blocked",     label: "Blocked" },
  ];

  return (
    <div className="iz-card p-5 space-y-4" data-testid={`stage-detail-${s.key}`}>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Stage {s.num}</p>
          <h3 className="font-display text-xl text-white mt-1">{s.label}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${pillClass(stagePillTone(stage.status, false))}`}>
            {(stage.status || "not_started").replace(/_/g, " ")}
          </span>
          {stage.pm_approved_at && <span className="text-[9px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-0.5 rounded flex items-center gap-1"><ShieldCheck className="w-3 h-3"/>PM Approved</span>}
        </div>
      </div>

      {s.payment_gate_95 && stage.status === "completed" && !sel.accounts_receipt_95_percent && (
        <div className="iz-card p-3 border-l-2 border-l-amber-500">
          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><Lock className="w-3 h-3"/>Downstream locked — awaiting 95% payment</p>
        </div>
      )}

      {stage.status === "blocked" && (
        <div className="iz-card p-3 border-l-2 border-l-red-500 bg-red-500/5" data-testid={`stage-blocked-reason-${s.key}`}>
          <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 flex items-center gap-1.5">
            <Lock className="w-3 h-3"/> Blocked
          </p>
          <p className="text-sm text-red-200 mt-1 whitespace-pre-wrap">
            {(stage.notes || "").trim() || <span className="italic text-red-300/60">No reason recorded.</span>}
          </p>
        </div>
      )}

      {/* Status update — freeform dropdown, no linear prerequisite */}
      <div className="border-t border-[#1a1a1a] pt-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Update Status</p>
        {canWrite ? (
          <div className="flex flex-wrap items-center gap-2">
            <select value={stage.status || "not_started"}
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={busy}
                    className="iz-input text-xs !py-1.5 !pr-8 max-w-[220px]"
                    data-testid={`stage-status-${s.key}`}>
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {s.requires_photo && (stage.files || []).length === 0 && stage.status !== "completed" && (
              <span className="text-[10px] font-mono text-gray-500">Tip: photos help audits (optional)</span>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-gray-500 italic">Only the assigned Project Manager, Site Supervisor, or Execution Manager can update.</p>
        )}
      </div>

      {/* Notes */}
      <div className="border-t border-[#1a1a1a] pt-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Stage Notes</p>
        {canWrite ? (
          <>
            <div className="relative">
              <textarea value={notes}
                        onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
                        placeholder="e.g. Waiting on client approval for handle finish"
                        className="iz-input w-full h-20 text-xs pr-10" data-testid={`stage-notes-${s.key}`}/>
              <button type="button"
                      onClick={recording ? stopRecord : startRecord}
                      disabled={transcribing || busy}
                      title={recording ? "Stop recording" : "Voice note (Kn / Hi / Te / En → English)"}
                      className={`absolute top-2 right-2 p-1.5 rounded border transition-colors ${
                        recording
                          ? "border-red-500/70 text-red-400 bg-red-500/10 animate-pulse"
                          : "border-[#262626] text-gray-400 hover:text-[#d4af37] hover:border-[#d4af37]/50"
                      }`}
                      data-testid={`stage-notes-mic-${s.key}`}>
                {transcribing ? <Loader2 className="w-4 h-4 animate-spin"/>
                 : recording ? <Square className="w-4 h-4"/>
                 : <Mic className="w-4 h-4"/>}
              </button>
            </div>
            {notesDirty && (
              <div className="mt-2 flex justify-end">
                <button onClick={saveNotes} disabled={busy} className="iz-btn-secondary text-xs"
                        data-testid={`stage-notes-save-${s.key}`}>
                  {busy ? "Saving…" : "Save notes"}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-xs text-gray-400 whitespace-pre-wrap">{stage.notes || <span className="text-gray-600 italic">No notes yet.</span>}</p>
        )}
      </div>

      {/* Photo upload */}
      <div className="border-t border-[#1a1a1a] pt-4 flex flex-wrap gap-2">
        {canWrite && (
          <>
            <button onClick={() => inputRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid={`upload-${s.key}`}>
              <Upload className="w-3.5 h-3.5"/>Upload photo
            </button>
            <input ref={inputRef} type="file" hidden multiple accept="image/*" onChange={e => uploadPhoto(e.target.files, false)}/>
            <button onClick={() => captureRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid={`capture-${s.key}`}>
              <Camera className="w-3.5 h-3.5"/>Capture photo
            </button>
            <input ref={captureRef} type="file" hidden accept="image/*" capture="environment"
                   onChange={e => uploadPhoto(e.target.files, false)}/>
            {s.requires_daily_photo && (
              <>
                <button onClick={() => dailyRef.current?.click()} className="iz-btn-secondary text-xs flex items-center gap-1.5" data-testid={`daily-${s.key}`}>
                  <Camera className="w-3.5 h-3.5"/>Daily photo
                </button>
                <input ref={dailyRef} type="file" hidden multiple accept="image/*" onChange={e => uploadPhoto(e.target.files, true)}/>
              </>
            )}
          </>
        )}
        {s.requires_pm_approval && stage.status === "completed" && !stage.pm_approved_at && (isPm && sel.project_manager_id === principalId || isExecMgr) && (
          <button onClick={pmApprove} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid={`pm-approve-${s.key}`}>
            <ShieldCheck className="w-3.5 h-3.5"/>PM Approve
          </button>
        )}
      </div>

      {(stage.files || []).length > 0 && (
        <div className="border-t border-[#1a1a1a] pt-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Attachments</p>
          <div className="grid grid-cols-4 gap-2">
            {stage.files.map(fid => (
              <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                 className="block aspect-square bg-[#0a0a0a] border border-[#1a1a1a] rounded overflow-hidden">
                <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover"/>
              </a>
            ))}
          </div>
        </div>
      )}
      {(stage.daily_photos || []).length > 0 && (
        <div className="border-t border-[#1a1a1a] pt-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Daily progress ({stage.daily_photos.length} days)</p>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {stage.daily_photos.map((d, i) => (
              <div key={`${d.date}-${i}`} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-gray-500">{d.date}</span>
                <div className="flex gap-1">
                  {d.file_ids.map(fid => (
                    <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                       className="block w-10 h-10 bg-[#0a0a0a] border border-[#1a1a1a] rounded overflow-hidden">
                      <img src={fileUrl(fid)} alt="" className="w-full h-full object-cover"/>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ComponentDispatchPanel({ sel, stageKey, stageNum, stageLabel, isSup, isPm, isExecMgr, principalId, onRefresh, focusDispatch = null, focusBulk = null }) {
  const [dispatches, setDispatches] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [note, setNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const { roleName } = useAuth();
  const canResolve = ["Purchase Manager", "Purchase Executive",
                      "Production Manager", "Procurement Manager"].includes(roleName) || isExecMgr;

  const canRaise =
    isExecMgr ||
    (isSup && sel.site_supervisor_id === principalId) ||
    (isPm && sel.project_manager_id === principalId);

  const loadDispatches = useCallback(async () => {
    try {
      const { data } = await api.get(`/execution/projects/${sel.id}/dispatches`);
      setDispatches(data || []);
    } catch { /* ignore */ }
  }, [sel.id]);

  useEffect(() => { loadDispatches(); }, [loadDispatches]);
  useEffect(() => {
    const stageIdx = STAGES.findIndex((x) => x.key === stageKey);
    setReqs(sel.execution_stages?.[stageIdx]?.material_requests || []);
  }, [sel.execution_stages, stageKey]);

  // Deep-link — scroll the highlighted dispatch into view.
  useEffect(() => {
    if (!focusDispatch && !focusBulk) return;
    const t = setTimeout(() => {
      const target = focusDispatch
        ? document.querySelector(`[data-testid="comp-dispatch-${focusDispatch}"]`)
        : document.querySelector(`[data-focus-bulk="${focusBulk}"]`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.classList.add("ring-2", "ring-[#d4af37]");
        setTimeout(() => target.classList.remove("ring-2", "ring-[#d4af37]"), 4000);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [focusDispatch, focusBulk, dispatches]);

  const pickFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const u = await uploadMany(Array.from(files));
      setAttachments((prev) => [...prev, ...u]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim() && attachments.length === 0) {
      toast.error("Add a note or a photo to raise a request");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/execution/projects/${sel.id}/material-requests`, {
        stage_key: stageKey,
        note: note.trim() || null,
        attachment_file_ids: attachments.map((a) => a.id),
      });
      toast.success("Material request raised — Purchase team notified");
      setNote(""); setAttachments([]);
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="iz-card p-5 space-y-5" data-testid={`stage-detail-${stageKey}`}>
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Stage {stageNum}</p>
        <h3 className="font-display text-xl text-white mt-1">{stageLabel}</h3>
        <p className="text-[11px] text-gray-500 mt-1 italic">
          Driven by Production, Procurement & Purchase — no manual status update here.
          Raise a request below if you need more material on-site.
        </p>
      </div>

      <div className="border-t border-[#1a1a1a] pt-4">
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] mb-3 flex items-center gap-2">
          <Truck className="w-3.5 h-3.5"/> Dispatch Log ({dispatches.length})
        </p>
        {dispatches.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No dispatches yet for this project.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {dispatches.map((d) => {
              const mat = d.material_category === "other" ? d.material_other : (d.material_category || "").replace(/_/g, " ");
              return (
                <div key={d.id} className="border border-[#1c1c1c] bg-[#080808] rounded-md p-3 transition-shadow"
                     data-testid={`comp-dispatch-${d.id}`}
                     data-focus-bulk={d.bulk_group_id || ""}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <p className="text-white text-sm capitalize">{mat}</p>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-400/30 bg-emerald-400/10 rounded px-1.5 py-0.5">
                      {d.status || "dispatched"}
                    </span>
                  </div>
                  {d.description && <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap">{d.description}</p>}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-[10px] text-gray-500 font-mono">
                    <div><span className="text-gray-600">Driver:</span> {d.driver_name || "—"}</div>
                    <div><span className="text-gray-600">Vehicle:</span> {d.vehicle_number || "—"}</div>
                    <div><span className="text-gray-600">When:</span> {d.dispatched_at ? new Date(d.dispatched_at).toLocaleString() : "—"}</div>
                    <div><span className="text-gray-600">By:</span> {d.dispatched_by_name}{d.dispatched_by_role ? ` (${d.dispatched_by_role})` : ""}</div>
                  </div>
                  {(d.attachment_file_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {d.attachment_file_ids.map((fid, i) => (
                        <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                           className="text-[10px] text-[#d4af37] hover:underline font-mono">📎 file {i + 1}</a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {canRaise && (
        <form onSubmit={submit} className="border-t border-[#1a1a1a] pt-4 space-y-3">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">Raise Material Request</p>
          <textarea value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="What do you need? e.g. 20 more shutter handles, brass finish"
                    className="iz-input w-full h-20 text-xs"
                    data-testid={`mat-request-note-${stageKey}`}/>
          <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer text-xs"
                 data-testid={`mat-request-attach-${stageKey}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Camera className="w-3.5 h-3.5"/>}
            {uploading ? "Uploading…" : "Attach photos"}
            <input type="file" hidden multiple accept="image/*,application/pdf"
                   onChange={(e) => pickFiles(e.target.files)}/>
          </label>
          {attachments.length > 0 && (
            <ul className="space-y-1">
              {attachments.map((a) => (
                <li key={a.id} className="text-[11px] text-gray-400 font-mono flex items-center gap-2">
                  <span className="text-[#d4af37]">✓</span> {a.original_filename}
                  <button type="button"
                          onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                          className="text-gray-500 hover:text-red-400"><X className="w-3 h-3"/></button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end">
            <button type="submit" disabled={busy}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid={`mat-request-submit-${stageKey}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
              Raise request
            </button>
          </div>
        </form>
      )}

      {reqs.length > 0 && (
        <div className="border-t border-[#1a1a1a] pt-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
            Material Requests Raised ({reqs.length})
          </p>
          <div className="space-y-2">
            {reqs.slice().reverse().map((r) => {
              const status = r.status || "open";
              const statusPill = status === "fulfilled"
                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                : status === "rejected"
                ? "border-red-400/40 bg-red-400/10 text-red-300"
                : "border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37]";
              const resolve = async (newStatus) => {
                const reason = window.prompt(
                  newStatus === "rejected"
                    ? "Reject reason (visible to the supervisor):"
                    : "Optional note for the supervisor (leave empty to skip):",
                  ""
                );
                if (reason === null) return;
                try {
                  await api.patch(`/execution/projects/${sel.id}/material-requests/${r.id}`,
                                  { status: newStatus, resolution_note: reason.trim() || null });
                  toast.success(`Request ${newStatus}`);
                  onRefresh();
                } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
              };
              return (
                <div key={r.id} className="border border-[#1c1c1c] bg-[#080808] rounded p-2 text-xs"
                     data-testid={`mat-request-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-gray-300 flex-1">{r.note || <span className="italic text-gray-500">(photos only)</span>}</p>
                    <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${statusPill}`}>
                      {status}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 font-mono">
                    <span>{r.raised_by_name}</span>
                    <span>·</span>
                    <span className="text-[#d4af37]">{r.raised_by_role}</span>
                    <span>·</span>
                    <span>{r.raised_at ? new Date(r.raised_at).toLocaleString() : "—"}</span>
                  </div>
                  {(r.attachment_file_ids || []).length > 0 && (
                    <div className="flex gap-2 mt-1">
                      {r.attachment_file_ids.map((fid, i) => (
                        <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer"
                           className="text-[10px] text-[#d4af37] hover:underline font-mono">📎 {i + 1}</a>
                      ))}
                    </div>
                  )}
                  {status !== "open" && (
                    <div className="mt-1.5 text-[10px] text-gray-500 border-t border-[#1a1a1a] pt-1.5">
                      <span className="font-mono">{status === "fulfilled" ? "✅ Fulfilled" : "❌ Rejected"}</span>
                      <span className="ml-2">by {r.resolved_by_name} ({r.resolved_by_role})</span>
                      {r.resolved_at && <span className="ml-2 font-mono">{new Date(r.resolved_at).toLocaleString()}</span>}
                      {r.resolution_note && <p className="text-gray-400 mt-0.5">{r.resolution_note}</p>}
                    </div>
                  )}
                  {status === "open" && canResolve && (
                    <div className="flex justify-end gap-1.5 mt-2">
                      <button onClick={() => resolve("rejected")}
                              className="iz-btn-secondary text-[10px]"
                              data-testid={`req-reject-${r.id}`}>Reject</button>
                      <button onClick={() => resolve("fulfilled")}
                              className="iz-btn-primary text-[10px]"
                              data-testid={`req-fulfil-${r.id}`}>Mark fulfilled</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function QASignoffPanel({ sel, onRefresh }) {
  const [snags, setSnags] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const addSnag = () => setSnags([...snags, { title: "", description: "", category: "installation" }]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/execution/projects/${sel.id}/qa-signoff`, { snags: snags.filter(s => s.title), remarks });
      toast.success("QA signed off");
      onRefresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="iz-card p-5" data-testid="qa-signoff">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">QA Sign-off & Handover</p>
      <div className="space-y-3">
        {snags.map((s, i) => (
          <div key={s._localId ?? `snag-row-${i}`} className="grid grid-cols-12 gap-2">
            <input value={s.title} onChange={e => setSnags(snags.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} placeholder="Snag title" className="iz-input col-span-4 text-xs !py-1"/>
            <input value={s.description} onChange={e => setSnags(snags.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} placeholder="Description" className="iz-input col-span-6 text-xs !py-1"/>
            <select value={s.category} onChange={e => setSnags(snags.map((x, idx) => idx === i ? { ...x, category: e.target.value } : x))} className="iz-input col-span-2 text-xs !py-1">
              {["design", "cnc", "purchase", "installation", "electrical", "other"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        ))}
        <button onClick={addSnag} className="iz-btn-secondary text-xs" data-testid="qa-add-snag">+ Add snag</button>
        <textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} className="iz-input" placeholder="QA remarks"/>
        <div className="flex justify-end">
          <button onClick={submit} disabled={busy} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="qa-submit">
            <ShieldCheck className="w-3.5 h-3.5"/>{busy ? "Signing…" : "Sign off & Trigger Snag Module"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignModal({ sel, pms, supervisors, onClose, onDone }) {
  const [pmId, setPmId] = useState(sel.project_manager_id || "");
  const [supId, setSupId] = useState(sel.site_supervisor_id || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!pmId || !supId) { setErr("Both required"); return; }
    setBusy(true);
    try {
      await api.post(`/execution/projects/${sel.id}/assign`, { project_manager_id: pmId, site_supervisor_id: supId });
      onDone();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="iz-card w-full max-w-lg p-8 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Assign</p>
        <h2 className="font-display text-2xl text-white mb-6">{sel.project_name}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Project Manager</label>
            <select value={pmId} onChange={e => setPmId(e.target.value)} className="iz-input" data-testid="pm-select">
              <option value="">Select…</option>
              {pms.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Site Supervisor</label>
            <select value={supId} onChange={e => setSupId(e.target.value)} className="iz-input" data-testid="sup-select">
              <option value="">Select…</option>
              {supervisors.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {err && <div className="text-sm text-red-400">{err}</div>}
          <div className="flex justify-end gap-2 pt-4 border-t border-[#1a1a1a]">
            <button onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button onClick={submit} disabled={busy} className="iz-btn-primary" data-testid="assign-submit">{busy ? "Assigning…" : "Confirm"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Weekly Reports History Panel (CRM only)
// -----------------------------------------------------------------------------
function WeeklyReportsHistoryPanel({ projectId, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); setLoading(false); return; }
    try {
      const { data } = await api.get(`/projects/${projectId}/reports/weekly`);
      setRows(data || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="iz-card p-5" data-testid="weekly-reports-history-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            CRM · Weekly Reports History
          </p>
          <h3 className="font-display text-xl text-white mt-0.5">
            {rows.length} report{rows.length === 1 ? "" : "s"}
          </h3>
        </div>
        <button onClick={onOpen}
                className="iz-btn-primary text-xs flex items-center gap-1.5"
                data-testid="wr-history-new-btn">
          <FileText className="w-3.5 h-3.5"/> New Report
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center italic border border-dashed border-[#262626] rounded-md">
          No weekly reports yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]" data-testid="wr-history-table">
            <thead className="text-[10px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#262626]">
              <tr>
                <th className="text-left py-2 px-2">Week Of</th>
                <th className="text-left py-2 px-2">Status</th>
                <th className="text-left py-2 px-2">Sent</th>
                <th className="text-left py-2 px-2">Read Receipt</th>
                <th className="text-left py-2 px-2">Sent By</th>
                <th className="text-left py-2 px-2">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#161616]"
                    data-testid={`wr-row-${r.id}`}>
                  <td className="py-2 px-2 text-white">{r.week_of || "—"}</td>
                  <td className="py-2 px-2">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${
                      r.status === "sent"
                        ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
                        : r.auto_created
                          ? "border-[#d4af37]/40 text-[#d4af37] bg-[#d4af37]/5"
                          : "border-gray-500/40 text-gray-400 bg-gray-500/5"
                    }`}>
                      {r.status === "sent"
                        ? "Sent"
                        : r.auto_created
                          ? "Auto-draft"
                          : "Draft"}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-gray-400">{fmt(r.sent_at)}</td>
                  <td className="py-2 px-2">
                    {r.viewed_at ? (
                      <span className="text-emerald-400 flex items-center gap-1 text-[11px]"
                            data-testid={`wr-viewed-${r.id}`}>
                        <Check className="w-3 h-3"/> Viewed {fmt(r.viewed_at)}
                        {r.view_count > 1 && (
                          <span className="text-gray-500 ml-1">· {r.view_count}×</span>
                        )}
                      </span>
                    ) : r.status === "sent" ? (
                      <span className="text-gray-500 text-[11px]">Not opened yet</span>
                    ) : (
                      <span className="text-gray-600 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-gray-400">{r.sent_by_name || r.created_by_name || "—"}</td>
                  <td className="py-2 px-2">
                    {r.pdf_file_id ? (
                      <a href={fileUrl(r.pdf_file_id)}
                         target="_blank" rel="noreferrer"
                         className="text-[#d4af37] hover:underline text-[11px]"
                         data-testid={`wr-pdf-${r.id}`}>
                        Download
                      </a>
                    ) : (
                      <span className="text-gray-600 text-[11px]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

